const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/**
 * Tiny TTL cache + in-flight dedupe for idempotent GETs that mount-thrash
 * (e.g. DashNav re-mounting on every skeleton→loaded transition and route change).
 *
 *   - Cached value served until `ttlMs` elapses.
 *   - Concurrent callers share the same in-flight promise.
 *   - Errors aren't cached; the next call retries.
 *
 * Use `invalidateCache(key)` after a mutation that changes the cached resource.
 */
const _apiCache = new Map(); // key -> { value, expiresAt } | { inFlight }

/**
 * `keyOrFn` is either a string (for 0-arg endpoints) or a function that maps
 * the call args to a unique key string (for endpoints with parameters like
 * page/limit/reviewerId).
 */
function cachedGet(keyOrFn, fetcher, ttlMs) {
  return (...args) => {
    const key = typeof keyOrFn === "function" ? keyOrFn(...args) : keyOrFn;
    const now = Date.now();
    const entry = _apiCache.get(key);
    if (entry?.inFlight) return entry.inFlight;
    if (entry && entry.expiresAt > now) return Promise.resolve(entry.value);

    const inFlight = fetcher(...args).then(
      (value) => {
        _apiCache.set(key, { value, expiresAt: Date.now() + ttlMs });
        return value;
      },
      (err) => {
        _apiCache.delete(key);
        throw err;
      }
    );
    _apiCache.set(key, { inFlight });
    return inFlight;
  };
}

export function invalidateCache(key) {
  _apiCache.delete(key);
}

export function invalidateCachePrefix(prefix) {
  for (const k of _apiCache.keys()) {
    if (k.startsWith(prefix)) _apiCache.delete(k);
  }
}

/**
 * Wrapper around fetch that includes credentials and handles JSON.
 */
export async function apiFetch(endpoint, options = {}) {
  const token = localStorage.getItem("reviewly_token");

  const config = {
    ...options,
    credentials: "include",
    headers: {
      // Don't set Content-Type for FormData — the browser sets it with the boundary.
      ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  };

  if (options.body instanceof FormData) {
    config.body = options.body;
  } else if (options.body && typeof options.body === "object") {
    config.body = JSON.stringify(options.body);
  }

  let res;
  try {
    res = await fetch(`${API_BASE}${endpoint}`, config);
  } catch (cause) {
    const err = new Error("Unable to reach the server. Check your connection and try again.");
    err.status = 0;
    err.isNetworkError = true;
    err.cause = cause;
    throw err;
  }

  const responseText = await res.text();
  let data = {};
  if (responseText) {
    try {
      data = JSON.parse(responseText);
    } catch (_) {
      data = { message: res.ok ? "Invalid server response" : `Server request failed (${res.status})` };
    }
  }

  if (!res.ok) {
    const err = new Error(data.message || "API request failed");
    err.status = res.status;
    err.apiResponse = data;
    throw err;
  }

  return data;
}

export function isAuthenticationError(err) {
  return err?.status === 401;
}

// ── Auth ──
export const authAPI = {
  googleLogin: (token) =>
    apiFetch("/auth/google-login", { method: "POST", body: { token } }),
  googleCodeLogin: (code) =>
    apiFetch("/auth/google-code-login", { method: "POST", body: { code } }),
  getMe: () => apiFetch("/auth/me"),
  updateMe: (data) =>
    apiFetch("/auth/me", { method: "PUT", body: data }),
  logout: () => apiFetch("/auth/logout", { method: "POST" }),
};

// ── Reviewers ──
// Reviewer catalog is admin-curated and changes rarely; 10 min TTL is safe.
const REVIEWERS_PREFIX = "reviewers:all:";
export const reviewerAPI = {
  getAll: cachedGet(
    (page = 1, limit = 50) => `${REVIEWERS_PREFIX}p${page}:l${limit}`,
    (page = 1, limit = 50) => apiFetch(`/reviewers?page=${page}&limit=${limit}`),
    10 * 60 * 1000
  ),
  getById: cachedGet(
    (id) => `reviewers:byId:${id}`,
    (id) => apiFetch(`/reviewers/${id}`),
    10 * 60 * 1000
  ),
  getBySlug: cachedGet(
    (slug) => `reviewers:bySlug:${slug}`,
    (slug) => apiFetch(`/reviewers/slug/${slug}`),
    10 * 60 * 1000
  ),
};

// ── Library (bookmarks) ──
const LIBRARY_KEY = "library:bookmarks";
export const libraryAPI = {
  get: cachedGet(LIBRARY_KEY, () => apiFetch("/library"), 5 * 60 * 1000),
  add: (reviewerId) =>
    apiFetch(`/library/${reviewerId}`, { method: "POST" }).then((r) => {
      invalidateCache(LIBRARY_KEY);
      return r;
    }),
  remove: (reviewerId) =>
    apiFetch(`/library/${reviewerId}`, { method: "DELETE" }).then((r) => {
      invalidateCache(LIBRARY_KEY);
      return r;
    }),
};

// ── Exams ──
const USER_HISTORY_PREFIX = "exam:user-history:";
const REVIEWER_PROGRESS_PREFIX = "exam:reviewer-progress:";
const DASHBOARD_KEY = "dashboard:summary";

function invalidateAfterExamMutation() {
  invalidateCache(DASHBOARD_KEY);
  invalidateCachePrefix(USER_HISTORY_PREFIX);
  invalidateCachePrefix(REVIEWER_PROGRESS_PREFIX);
}

export const examAPI = {
  start: (reviewerId, restart = false, retakeFrom = null) =>
    apiFetch(`/exams/${reviewerId}/start`, {
      method: "POST",
      body: (restart || retakeFrom) ? { ...(restart && { restart: true }), ...(retakeFrom && { retakeFrom }) } : undefined,
    }).then((r) => {
      invalidateAfterExamMutation();
      return r;
    }),
  saveAnswer: (attemptId, questionIndex, selectedAnswer, remainingSeconds) =>
    apiFetch(`/exams/attempts/${attemptId}/answer`, {
      method: "PUT",
      body: {
        questionIndex,
        selectedAnswer,
        ...(Number.isFinite(remainingSeconds) ? { remainingSeconds } : {}),
      },
    }),
  saveAnswers: (attemptId, answers, remainingSeconds, currentIndex) =>
    apiFetch(`/exams/attempts/${attemptId}/answer`, {
      method: "PUT",
      body: {
        answers,
        ...(Number.isFinite(remainingSeconds) ? { remainingSeconds } : {}),
        ...(Number.isInteger(currentIndex) ? { currentIndex } : {}),
      },
    }),
  pause: (attemptId, remainingSeconds, currentIndex) =>
    apiFetch(`/exams/attempts/${attemptId}/pause`, {
      method: "PUT",
      body: { remainingSeconds, currentIndex },
    }).then((r) => {
      invalidateAfterExamMutation();
      return r;
    }),
  submit: (attemptId, remainingSeconds, answers) =>
    apiFetch(`/exams/attempts/${attemptId}/submit`, {
      method: "POST",
      body: {
        ...(remainingSeconds != null ? { remainingSeconds } : {}),
        ...(answers && typeof answers === "object" ? { answers } : {}),
      },
    }).then((r) => {
      invalidateAfterExamMutation();
      return r;
    }),
  getResult: (attemptId) =>
    apiFetch(`/exams/attempts/${attemptId}`),
  getReview: (attemptId) =>
    apiFetch(`/exams/attempts/${attemptId}/review`),
  getUserHistory: cachedGet(
    (page = 1, limit = 20) => `${USER_HISTORY_PREFIX}p${page}:l${limit}`,
    (page = 1, limit = 20) =>
      apiFetch(`/exams/attempts/user/history?page=${page}&limit=${limit}`),
    2 * 60 * 1000
  ),
  getReviewerProgress: cachedGet(
    (reviewerId) => `${REVIEWER_PROGRESS_PREFIX}${reviewerId}`,
    (reviewerId) => apiFetch(`/exams/attempts/user/progress/${reviewerId}`),
    2 * 60 * 1000
  ),
  generateShareLink: (attemptId) =>
    apiFetch(`/exams/attempts/${attemptId}/share`, { method: "POST" }),
  uploadShareImage: (attemptId, imageDataUrl) =>
    apiFetch(`/exams/attempts/${attemptId}/share-image`, {
      method: "POST",
      body: { imageData: imageDataUrl },
    }),
};

// ── Shared results (public, no auth) ──
export const sharedAPI = {
  getResult: (shareToken) =>
    fetch(`${API_BASE}/exams/shared/${encodeURIComponent(shareToken)}`)
      .then((r) => r.json()),
};

// ── Trial Assessment ──
export const trialAPI = {
  getStatus: () => apiFetch("/trial-assessment/status"),
  getReviewers: () => apiFetch("/trial-assessment/reviewers"),
  skip: (examType) =>
    apiFetch("/trial-assessment/skip", {
      method: "POST",
      body: examType ? { examType } : undefined,
    }),
  start: (reviewerId, retake = false, examType) => {
    const body = {};
    if (retake) body.retake = true;
    if (examType) body.examType = examType;
    return apiFetch(`/trial-assessment/${reviewerId}/start`, {
      method: "POST",
      body: Object.keys(body).length ? body : undefined,
    });
  },
  saveAnswer: (attemptId, questionIndex, selectedAnswer, remainingSeconds) =>
    apiFetch(`/trial-assessment/attempts/${attemptId}/answer`, {
      method: "PUT",
      body: {
        questionIndex,
        selectedAnswer,
        ...(Number.isFinite(remainingSeconds) ? { remainingSeconds } : {}),
      },
    }),
  saveAnswers: (attemptId, answers, remainingSeconds, currentIndex) =>
    apiFetch(`/trial-assessment/attempts/${attemptId}/answer`, {
      method: "PUT",
      body: {
        answers,
        ...(Number.isFinite(remainingSeconds) ? { remainingSeconds } : {}),
        ...(Number.isInteger(currentIndex) ? { currentIndex } : {}),
      },
    }),
  pause: (attemptId, remainingSeconds, currentIndex) =>
    apiFetch(`/trial-assessment/attempts/${attemptId}/pause`, {
      method: "PUT",
      body: { remainingSeconds, currentIndex },
    }),
  submit: (attemptId, remainingSeconds, answers) =>
    apiFetch(`/trial-assessment/attempts/${attemptId}/submit`, {
      method: "POST",
      body: {
        ...(remainingSeconds != null ? { remainingSeconds } : {}),
        ...(answers && typeof answers === "object" ? { answers } : {}),
      },
    }),
  abandon: (attemptId) =>
    apiFetch(`/trial-assessment/attempts/${attemptId}/abandon`, { method: "POST" }),
  getResult: (attemptId) =>
    apiFetch(`/trial-assessment/attempts/${attemptId}/result`),
};

// ── Announcement entries ──
const WHATS_NEW_UNREAD_KEY = "whats-new:unread";
const WHATS_NEW_LIST_KEY = "whats-new:list";
export const announcementEntriesAPI = {
  list: cachedGet(WHATS_NEW_LIST_KEY, () => apiFetch("/whats-new"), 5 * 60 * 1000),
  getUnread: cachedGet(WHATS_NEW_UNREAD_KEY, () => apiFetch("/whats-new/unread"), 5 * 60 * 1000),
  markSeen: () =>
    apiFetch("/whats-new/seen", { method: "POST" }).then((r) => {
      invalidateCache(WHATS_NEW_UNREAD_KEY);
      return r;
    }),
};

// ── Support ──
export const supportAPI = {
  submitContact: (data) =>
    apiFetch("/support/contact", { method: "POST", body: data }),
  submitHelp: (data) =>
    apiFetch("/support/help", { method: "POST", body: data }),
  submitReport: (data) =>
    apiFetch("/support/report", { method: "POST", body: data }),
};

// ── Payments (manual GCash upgrade flow) ──
export const paymentsAPI = {
  /**
   * Submit a GCash payment confirmation. The proof image (if any) is sent as
   * a multipart file upload — multer+CloudinaryStorage on the server handles
   * the Cloudinary upload and returns the secure URL for storage.
   * Public endpoint; works pre-login.
   */
  submitUpgrade: ({ email, gcashRef, gcashName, proof }) => {
    const formData = new FormData();
    formData.append("email", email);
    formData.append("gcashRef", gcashRef);
    formData.append("gcashName", gcashName);
    if (proof) formData.append("proofImage", proof);
    return apiFetch("/payments/upgrade", { method: "POST", body: formData });
  },
};

// ── Dashboard ──
// Dashboard summary is volatile (sprint state, recent activity). Short TTL +
// explicit invalidation after every mutation that affects it.
export const dashboardAPI = {
  get: cachedGet(DASHBOARD_KEY, () => apiFetch("/dashboard"), 60 * 1000),
  generateSprint: () =>
    apiFetch("/dashboard/sprint/generate", { method: "POST" }).then((r) => {
      invalidateCache(DASHBOARD_KEY);
      return r;
    }),
  abandonSprint: () =>
    apiFetch("/dashboard/sprint", { method: "DELETE" }).then((r) => {
      invalidateCache(DASHBOARD_KEY);
      return r;
    }),
  startTask: (taskId) =>
    apiFetch(`/dashboard/sprint/tasks/${taskId}/start`, { method: "POST" }).then((r) => {
      invalidateCache(DASHBOARD_KEY);
      return r;
    }),
  saveTaskAnswer: (taskId, questionIndex, selectedAnswer) =>
    apiFetch(`/dashboard/sprint/tasks/${taskId}/answer`, {
      method: "PUT",
      body: { questionIndex, selectedAnswer },
    }),
  submitTask: (taskId, answers) =>
    apiFetch(`/dashboard/sprint/tasks/${taskId}/submit`, {
      method: "POST",
      body: { answers },
    }).then((r) => {
      invalidateCache(DASHBOARD_KEY);
      return r;
    }),
  getTaskReview: (taskId) =>
    apiFetch(`/dashboard/sprint/tasks/${taskId}/review`),
};

/* ── Announcement banner (public) ───────────── */
export const announcementAPI = {
  get: cachedGet("announcement:banner", () => apiFetch("/settings/announcement"), 5 * 60 * 1000),
};
